import type { ResolvedEdible } from "api-types";
import axios from "axios";

import React, { type PropsWithChildren, useEffect } from "react";

import { AxiosClientContext, useAxiosClient } from "igfd-commons/src/http";

import { useRestaurant } from "../hooks/useRestaurant";

/**
 * Injects x-restaurant-uuid into API requests so they can hit
 * restaurant-authorized endpoints. Firebase auth is added upstream; this
 * provider is mounted by RestaurantManagementLayout.
 */
export const AxiosRestaurantAuthZProvider = ({
  children,
}: PropsWithChildren) => {
  const clientWithAuth = useAxiosClient({ withAuth: true });
  const restaurant = useRestaurant();

  useEffect(() => {
    const authInterceptorId = clientWithAuth.interceptors.request.use(
      async (config) => {
        config.headers["x-restaurant-uuid"] = restaurant.uuid;
        return config;
      }
    );
    return () => {
      // FIXME ensure that we eject only when the restaurantUuid changes.
      clientWithAuth.interceptors.request.eject(authInterceptorId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant.uuid]);

  return <>{children}</>;
};

const emptyResolvedEdible: ResolvedEdible = {
  allAllergens: [],
  allMayContainAllergens: [],
  allIngredients: [],
  allPreferences: [],
  mostRestrictivePreference: null,
};

// Storybook has no backend. Keep unexpected requests visible, but satisfy the
// resolution query used by dish and product drawers so their interaction
// stories can exercise the drawer rather than fail on an intentional stub.
const storybookStubClient = axios.create();
storybookStubClient.defaults.adapter = (config) => {
  if (config.url === "/menus/resolve-edible") {
    return Promise.resolve({
      data: emptyResolvedEdible,
      status: 200,
      statusText: "OK",
      headers: {},
      config,
    });
  }

  return Promise.reject(new Error("storybook: network disabled"));
};

export const NoAuthAxiosClientProvider = ({ children }: PropsWithChildren) => (
  <AxiosClientContext.Provider
    value={{
      clientWithAuth: storybookStubClient,
      clientWithoutAuth: storybookStubClient,
    }}
  >
    {children}
  </AxiosClientContext.Provider>
);
